import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Chat.css"; // Assurez-vous que ce fichier CSS est correctement configuré
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
  AppLayout,
  BreadcrumbGroup,
  Container,
  ContentLayout,
  Header,
  HelpPanel,
  Link,
  SideNavigation,
  SplitPanel,
  Button,
  FormField,
  Textarea, // Remplacez Input par Textarea
  SpaceBetween,
  Box,
  ButtonGroup,
  StatusIndicator,
  ColumnLayout,
  Icon,
  Form,
  ExpandableSection,
  Modal,
  Badge,
} from "@cloudscape-design/components";
import ChatBubble from "@cloudscape-design/chat-components/chat-bubble";
import Avatar from "@cloudscape-design/chat-components/avatar";
import { useAuth } from "../AuthContext";
import ReactMarkdown from 'react-markdown';
import { Document, Page, pdfjs } from 'react-pdf'; // Import PDF viewer components
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs`;

// Create a configurable axios instance for API calls
const api = axios.create({
  baseURL: '', // Default to relative paths
});

function decodeUnicode(str) {
  return str.replace(/\\u[\dA-F]{4}/gi, (match) => {
    return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
  });
}

const Chat = ({ onLogout }) => {
  const navigate = useNavigate();
  const { user, resetInactivityTimer, refreshToken, isAuthenticated } = useAuth();
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isNavigationOpen, setIsNavigationOpen] = useState(true);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState(true);
  const [isSplitPanelSide, setIsSplitPanelSide] = useState("side");
  const [isSplitPanelSize, setIsSplitPanelSize] = useState(400); // Increased default width
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfUrl, setPdfUrl] = useState("");
  const [currentDocument, setCurrentDocument] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false); // State for modal visibility
  const [apiInitialized, setApiInitialized] = useState(false);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= numPages) {
      setPageNumber(page);
    }
  };

  const openHelpPanelWithDocument = (documentName, page) => {
    setCurrentDocument(documentName);
    setPdfUrl(`http://localhost:3000/${documentName}`);
    setPageNumber(page);
    setIsToolsOpen(true);
    setIsNavigationOpen(false);
    setIsSplitPanelOpen(false);
  };

  const extractSources = (text) => {
    const sourceRegex = /(.*\.pdf), page (\d+),/g;
    const sources = [];
    let match;
    while ((match = sourceRegex.exec(text)) !== null) {
      sources.push({
        document: match[1],
        page: parseInt(match[2], 10),
      });
    }
    return sources;
  };

  // Set up API interceptors only once
  useEffect(() => {
    if (apiInitialized) return;

    // Set up request interceptor to add auth token
    api.interceptors.request.use(
      (config) => {
        const accessToken = user?.access_token || user?.token;
        if (accessToken) {
          console.log(`Adding token to request: ${config.url}`);
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          console.log(`No token available for request: ${config.url}`);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Set up response interceptor to handle token refresh
    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            console.log("Token expired, attempting to refresh...");
            const newToken = await refreshToken();
            
            if (newToken) {
              console.log("Token refreshed, retrying request");
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );

    setApiInitialized(true);
  }, [user, refreshToken, apiInitialized]);

  // Fetch sessions with proper error handling
  const fetchSessions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      console.log("Not authenticated, skipping session fetch");
      return;
    }

    try {
      const response = await api.get("/api/sessions/list/");
      setSessions(response.data);
    } catch (error) {
      console.error("Sessions list error:", error);
      
      if (error.response?.status === 401) {
        console.log("Unauthorized, token might be expired");
      }
    }
  }, [user, isAuthenticated]);

  // Create a new session when the component mounts
  useEffect(() => {
    const createNewSession = async () => {
      if (!isAuthenticated || !user) {
        console.log("Not authenticated yet, waiting to create session");
        return;
      }

      const newSessionId = uuidv4();
      setSessionId(newSessionId);
      
      try {
        await api.post("/api/sessions/create/", { sessionId: newSessionId });
        console.log("New session created:", newSessionId);
        fetchSessions();
      } catch (error) {
        console.error("Session creation error:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    if (isAuthenticated && user) {
      createNewSession();
    }
  }, [isAuthenticated, user, fetchSessions]);

  // Periodic session refresh
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchSessions();
    const intervalId = setInterval(fetchSessions, 30000); // Reduced frequency to 30 seconds
    
    return () => clearInterval(intervalId);
  }, [fetchSessions, isAuthenticated]);

  // Load a specific session 
  const loadSession = useCallback(async (sessionId) => {
    if (!sessionId || !isAuthenticated) return;
    
    setSessionId(sessionId);
    setIsLoading(true);
    
    try {
      const response = await api.get(`/api/sessions/${sessionId}/history/`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error getting history:", error);
      
      if (error.response?.status === 404) {
        console.log("Session history not found");
        setHistory([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Send a message with proper error handling
  const sendMessage = useCallback(async () => {
    if (!message.trim() || !sessionId || !isAuthenticated) return;
    
    setIsLoading(true);
    setHistory((prevHistory) => [
      ...prevHistory,
      { sender: "user", text: message },
    ]);
    setHistory((prevHistory) => [
      ...prevHistory,
      { sender: "bot", text: "Loading ...", isLoading: true },
    ]);

    try {
      const response = await api.post("/api/chat/send/", { 
        message, 
        sessionId, 
        history 
      });

      setHistory((prevHistory) => [
        ...prevHistory.slice(0, -1),
        { sender: "bot", text: response.data.output, isLoading: false },
      ]);
    } catch (error) {
      console.error("Model communication error:", error);

      setHistory((prevHistory) => [
        ...prevHistory.slice(0, -1),
        { sender: "bot", text: "Error. Please try later.", isLoading: false },
      ]);
    } finally {
      setIsLoading(false);
      setMessage("");
      resetInactivityTimer();
    }
  }, [message, sessionId, history, resetInactivityTimer, isAuthenticated]);

  const handleCopy = useCallback((text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => console.log("Text copied to clipboard"))
      .catch((error) => console.error("Failed to copy text: ", error));
  }, []);

  // Reset inactivity timer on user activity
  useEffect(() => {
    if (!user) return;

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];
    const resetTimer = () => resetInactivityTimer();

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [user, resetInactivityTimer]);

  // Show loading state while initializing
  if (isInitializing && isAuthenticated) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Initializing chat session...</p>
      </div>
    );
  }

  return (
    <>
      <AppLayout
        disableContentPaddings
        navigationHide={false}
        toolsHide={false}
        splitPanelPreferences={{ position: isSplitPanelSide }}
        splitPanelSize={isSplitPanelSize}
        splitPanelOpen={isSplitPanelOpen}
        onSplitPanelToggle={() => setIsSplitPanelOpen(!isSplitPanelOpen)}
        onSplitPanelPreferencesChange={({ detail }) =>
          setIsSplitPanelSide(detail.position)
        }
        onSplitPanelResize={({ detail }) => setIsSplitPanelSize(detail.size)}
        navigationOpen={isNavigationOpen}
        onNavigationChange={({ detail }) => setIsNavigationOpen(detail.open)}
        navigation={
          <SideNavigation
            header={{
              href: "#",
              text: "Adexgénie Agent SNZ",
            }}
            items={[
              { type: "link", text: "Dashboard", href: "/dashboard" },
              { type: "link", text: "Chat", href: "/chat" },
              {
                type: "section",
                text: "Adexgénie Agents",
                items: [
                  { type: "link", text: "Add Agent", href: "/Wizard" },
                  { type: "link", text: "List Agents", href: "/agents" },
                  { type: "link", text: "Agents Plugins", href: "/plugins" },
                  {
                    type: "link",
                    text: "Agents Workflows",
                    href: "/workflows",
                    info: <Badge color="grey">EXP</Badge>,
                  },
                ],
              },
              { type: "divider" },
              {
                type: "section",
                text: "Knowledge & Files",
                items: [
                  {
                    type: "link",
                    text: "Knowledge Bases",
                    href: "/knowledge-bases",
                    info: <Badge color="green">PRO</Badge>,
                  },
                  { type: "link", text: "My Files", href: "/files" },
                  {
                    type: "link",
                    text: "My Templates",
                    href: "/templates",
                    info: <Badge color="green">PRO</Badge>,
                  },
                ],
              },
              { type: "divider" },
              {
                type: "section",
                text: "Adexgénie Stats",
                items: [
                  { type: "link", text: "Telephony", href: "/telephony" },
                  { type: "link", text: "List Chat", href: "/chat" },
                  {
                    type: "link",
                    text: "List Integrations",
                    href: "/addons",
                    info: <Badge color="green">PRO</Badge>,
                  },
                ],
              },
            ]}
          />
        }
        toolsOpen={isToolsOpen}
        onToolsChange={({ detail }) => setIsToolsOpen(detail.open)}
        tools={
          <HelpPanel header={<h2>{currentDocument ? `${currentDocument} - Page ${pageNumber}` : 'Informations'}</h2>}>
            <SpaceBetween direction="vertical" size="m" style={{ padding: '0', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box textAlign="center">
                <Button onClick={() => goToPage(pageNumber - 1)} disabled={pageNumber <= 1}  iconName="angle-left" variant="icon" />
   
                <span style={{ margin: '0 10px' }}>
                  Page {pageNumber} sur {numPages}
                </span>
                <Button onClick={() => goToPage(pageNumber + 1)} disabled={pageNumber >= numPages} iconName="angle-right" variant="icon" />
            
              </Box>

              <Box style={{ width: '100%', display: 'flex', justifyContent: 'center', overflow: 'auto', position: 'relative' }}>
                {pdfUrl && (
                  <div style={{ position: 'relative' }}>
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                    >
                      <Page pageNumber={pageNumber} width={isSplitPanelSize - 40} />
                    </Document>
                    {/* Icône de preview en permanence */}
                    <button
                      iconName="view"
                      variant="icon"
                      onClick={() => setIsModalVisible(true)} // Ouvre le modal au clic
                      style={{
                        position: 'absolute',
                        top: '40%',
                        right: '30%',
                        zIndex: 100000,
                        color: 'rgba(0, 119, 204, 0.66)', // Bleu transparent
                        backgroundColor: 'transparent',
                        border:"none",
                        borderRadius: '50%',
                        padding: '30px',
                      }}
                    >
                      <img
                        src="/icon-preview.png" // Chemin vers l'image dans le dossier public
                        alt="Preview"
                        style={{
                          width: '40px', // Ajustez la taille de l'image selon vos besoins
                          height: '40px',
                          opacity:"0.5",
                        }}
                      />
                    </button>
                  </div>
                )}
              </Box>
            </SpaceBetween>
          </HelpPanel>
        }
        content={
          <div style={{ top: "-4px", position: "relative" }}>
            <ContentLayout
              breadcrumbs={
                <BreadcrumbGroup
                  items={[
                    { text: "Home", href: "/dashboard" },
                    { text: "Chat", href: "/chat" },
                  ]}
                  onFollow={(event) => {
                    event.preventDefault();
                    navigate(event.detail.href);
                  }}
                />
              }
              defaultPadding
              headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
              header={
                <Header
                  variant="h1"
                  info={<Link variant="info" onFollow={() => setIsToolsOpen(true)}>Info</Link>}
                  description="Chat with your Adexgénie AI assistant."
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button iconName="refresh" onClick={() => { setHistory([]); setMessage(''); }}>
                        New Chat
                      </Button>
                    </SpaceBetween>
                  }
                >
                  Chat
                </Header>
              }
            >
              <Container
                padding={{ top: "xs", bottom: "l" }}
                footer={
                  <form onSubmit={(e) => e.preventDefault()}>
                    <Form>
                      <SpaceBetween direction="vertical" size="xs">
                        <FormField label="Message">
                          <Textarea
                            value={message}
                            onChange={(e) => setMessage(e.detail.value)}
                            onKeyDown={(e) =>
                              e.detail.key === "Enter" &&
                              !isLoading &&
                              sendMessage()
                            }
                            placeholder="Tape your prompt..."
                            disabled={isLoading || !isAuthenticated}
                            rows={3} // Ajustez le nombre de lignes selon vos besoins
                          />
                        </FormField>
                        <Button
                          variant="primary"
                          onClick={sendMessage}
                          disabled={isLoading || !isAuthenticated}
                        >
                          {isLoading ? "Sending..." : "Send"}
                        </Button>
                      </SpaceBetween>
                    </Form>
                  </form>
                }
              >
                <Box
                  padding="m"
                  border={{ color: "border-container", width: "1px" }}
                  borderRadius="border-radius-container"
                  overflow="auto"
                  height="60vh"
                >
                  {!isAuthenticated ? (
                    <Box textAlign="center" color="text-body-secondary">
                      Please log in to use the chat.
                    </Box>
                  ) : history.length === 0 ? (
                    <Box textAlign="center" padding="xl">
                      <SpaceBetween size="l">
                        <div>
                          <Box variant="h1" textAlign="center">
                            <Icon name="gen-ai" variant="normal" size="big" /> Ask Adexgénie AI
                          </Box>
                          <Box variant="p" color="text-body-secondary" textAlign="center">
                            Start a conversation with your AI assistant. You can ask about your agents, documents, or workflows.
                          </Box>
                        </div>
                        <SpaceBetween direction="horizontal" size="xs" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
                          {['Summarize my agents', 'Explain my last call', 'Help me create a workflow'].map((prompt) => (
                            <Button
                              key={prompt}
                              variant="normal"
                              onClick={() => {
                                setMessage(prompt);
                                sendMessage();
                              }}
                            >
                              {prompt}
                            </Button>
                          ))}
                        </SpaceBetween>
                      </SpaceBetween>
                    </Box>
                  ) : (
                    history.map((msg, index) =>
                      msg.sender === "user" ? (
                        <ChatBubble
                          key={index}
                          ariaLabel={`${user.email} at ${new Date().toLocaleTimeString()}`}
                          type="outgoing"
                          avatar={
                            <Avatar
                              ariaLabel={user.username}
                              tooltipText={user.username}
                            />
                          }
                        >
                          {msg.text}
                        </ChatBubble>
                      ) : (
                        <ChatBubble
                          key={index}
                          ariaLabel={`Generative AI assistant at ${new Date().toLocaleTimeString()}`}
                          type="incoming"
                          actions={
                            !msg.isLoading && (
                              <ButtonGroup
                                ariaLabel="Chat bubble actions"
                                variant="icon"
                                items={[
                                  {
                                    type: "group",
                                    text: "Feedback",
                                    items: [
                                      {
                                        type: "icon-toggle-button",
                                        id: "helpful",
                                        iconName: "thumbs-up",
                                        pressedIconName: "thumbs-up-filled",
                                        text: "Helpful",
                                        pressed: true,
                                      },
                                      {
                                        type: "icon-toggle-button",
                                        id: "not-helpful",
                                        iconName: "thumbs-down",
                                        pressedIconName: "thumbs-down-filled",
                                        text: "Not helpful",
                                        pressed: false,
                                        disabled: true,
                                      },
                                    ],
                                  },
                                  {
                                    type: "icon-button",
                                    id: "copy",
                                    iconName: "copy",
                                    text: "Copy",
                                    onClick: () => handleCopy(msg.text),
                                    popoverFeedback: (
                                      <StatusIndicator type="success">
                                        Message copied
                                      </StatusIndicator>
                                    ),
                                  },
                                ]}
                              />
                            )
                          }
                          avatar={
                            <Avatar
                              color="gen-ai"
                              iconName="gen-ai"
                              ariaLabel="Generative AI assistant"
                              tooltipText="Generative AI assistant"
                            />
                          }
                          showLoadingBar={msg.isLoading}
                        >
                          <ReactMarkdown>{decodeUnicode(msg.text)}</ReactMarkdown>
                          {/* Afficher la section ExpandableSection uniquement si la réponse contient des sources */}
                          {msg.sender === "bot" && extractSources(msg.text).length > 0 && (
                            <ExpandableSection headerText="Sources View">
                              <ColumnLayout columns={{ default: 1, s: 1, m: 2, l: 2, xl: 2 }} variant="text-grid">
                                {extractSources(msg.text).map((source, idx) => (
                                  <div key={idx}>
                                    <Button
                                      onClick={() => openHelpPanelWithDocument(source.document, source.page)}
                                      iconName="external"
                                      style={{ marginLeft: '10px' }}
                                      variant="inline-link"
                                    >
                                      Voir {source.document}, page {source.page}
                                    </Button>
                                  </div>
                                ))}
                              </ColumnLayout>
                            </ExpandableSection>
                          )}
                        </ChatBubble>
                      )
                    )
                  )}
                </Box>
              </Container>
            </ContentLayout>
          </div>
        }
        splitPanel={
          <SplitPanel
            header="Last Sessions"
            key="split-panel"
            data-scrolled="blue"
          >
            {!isAuthenticated ? (
              <Box textAlign="center" color="text-body-secondary">
                Please log in to view sessions.
              </Box>
            ) : (
              <ColumnLayout
                columns={1}
                variant="text-grid"
                fontSize="body-s"
                fontWeight="light"
              >
                {Object.values(sessions).length === 0 ? (
                  <Box textAlign="center" color="text-body-secondary">
                    No sessions available.
                  </Box>
                ) : (
                  Object.values(sessions).map((session, index) => (
                    <div key={index}>
                      <Icon name="gen-ai" />{" "}
                      <Link
                        onFollow={() => loadSession(session.id)}
                        fontSize="body-s"
                        variant="info"
                      >
                        {session.id}
                      </Link>
                    </div>
                  ))
                )}
              </ColumnLayout>
            )}
          </SplitPanel>
        }
      />

      {/* Modal pour afficher le PDF en mode plus large */}
      <Modal
        onDismiss={() => setIsModalVisible(false)} // Ferme le modal
        visible={isModalVisible} // Contrôle la visibilité du modal
        size="large" // Taille du modal
        header={`${currentDocument} - Page ${pageNumber}`} // Titre du modal
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <Page pageNumber={pageNumber} width={800} /> {/* Largeur fixe pour le modal */}
        </Document>
      </Modal>
    </>
  );
};

export default Chat;
